import type { ChakraProps } from '@chakra-ui/react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { TokenType } from '@ui/components/AddTokenButton';
import { features } from '@ui/components/Features';
import { TokenIcon } from '@ui/components/TokenIcon';
import { getContractAddresses } from '@ui/config/base';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import type { FC } from 'react';
import { createContext, useContext } from 'react';

import { getPathInfo, useRealPathname } from './router';
import { useAtomBalance } from './useAtomBalance';
import { useBalance } from './useBalance';

export type CoinMode = 'cro' | 'atom';
export const coinModes: CoinMode[] = ['cro', 'atom'];

export const COIN_MODE: Record<
  CoinMode,
  {
    coinMode: CoinMode;
    name: string;
    Icon: FC<ChakraProps>;
    decimals: number;
    liquidName: string;
    liquidAddress: string;
    nftAddress: string;
    liquidTokenType: TokenType;
    LiquidIcon: FC<ChakraProps>;
    liquidDecimals: number;
    minStakeAmount: number;
  }
> = {
  cro: {
    coinMode: 'cro',
    name: 'CRO',
    Icon: (props) => {
      return (
        <TokenIcon token="cro" w="32px" h="32px" alt="cro icon" {...props} />
      );
    },
    decimals: 18,
    liquidName: 'LCRO',
    liquidAddress: getContractAddresses().liquidCro,
    nftAddress: getContractAddresses().venoNft,
    liquidTokenType: TokenType.LCRO,
    LiquidIcon: (props) => {
      return (
        <TokenIcon token="lcro" w="32px" h="32px" alt="lcro icon" {...props} />
      );
    },
    liquidDecimals: 18,
    minStakeAmount: 1,
  },
  atom: {
    coinMode: 'atom',
    name: 'ATOM',
    Icon: (props) => {
      return (
        <TokenIcon token="atom" w="32px" h="32px" alt="atom icon" {...props} />
      );
    },
    decimals: 6,
    liquidName: 'LATOM',
    liquidAddress: getContractAddresses().liquidAtom,
    nftAddress: getContractAddresses().atomNft,
    liquidTokenType: TokenType.LATOM,
    LiquidIcon: (props) => {
      return (
        <TokenIcon
          token="latom"
          w="32px"
          h="32px"
          alt="latom icon"
          {...props}
        />
      );
    },
    liquidDecimals: 6,
    minStakeAmount: 0.05,
  },
};

const coinModeContext = createContext<CoinMode | undefined>(undefined);

export const CoinModeProvider = coinModeContext.Provider;

export const useGlobalCoinMode = (): CoinMode => {
  const realPathname = useRealPathname();
  const pathInfo = getPathInfo(realPathname);

  if (isFeatureEnabled(features.Atom) && pathInfo.coinMode === 'atom') {
    return 'atom';
  }

  return 'cro';
};

export const useCoinMode = (): CoinMode => {
  const coinModeInContext = useContext(coinModeContext);
  const coinModeInGlobal = useGlobalCoinMode();

  return coinModeInContext ?? coinModeInGlobal;
};

export const useCoinModeBalance = (
  _coinMode?: CoinMode,
  options: UseQueryOptions<number | undefined> = {},
) => {
  const globalCoinMode = useCoinMode();
  const coinMode = _coinMode ?? globalCoinMode;

  const croBalance = useBalance({
    ...options,
    enabled: coinMode === 'cro' ? options.enabled : false,
  });

  const atomBalance = useAtomBalance({
    ...options,
    enabled: coinMode === 'atom' ? options.enabled : false,
  });

  if (coinMode === 'atom') {
    return atomBalance;
  }

  return croBalance;
};
